import React, { createContext, useContext, useState, useCallback } from 'react';
import { doc, getDoc, setDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { extractSizeStock, buildInitialStock } from '../utils/inventoryUtils';

const InventoryContext = createContext();

const DEFAULT_STOCK = 10;
const STOCK_SEED_VERSION = 1;

export const useInventory = () => {
  const ctx = useContext(InventoryContext);
  if (!ctx) throw new Error('useInventory must be used within InventoryProvider');
  return ctx;
};

export const InventoryProvider = ({ children }) => {
  const [inventory, setInventory] = useState({});

  const syncProductInventory = useCallback(async (product) => {
    if (!product?.id || !product?.name) return;
    const sizes = product.sizesAvailable || ['P', 'M', 'G', 'GG'];

    try {
      const docRef = doc(db, 'inventory', product.id);
      const docSnap = await getDoc(docRef);
      const needsSeed = !docSnap.exists()
        || docSnap.data().stockSeedVersion !== STOCK_SEED_VERSION;

      const updates = {
        name: product.name,
        price: product.price || null,
        updatedAt: serverTimestamp(),
      };

      if (needsSeed) {
        sizes.forEach((size) => {
          updates[size] = DEFAULT_STOCK;
        });
        updates.stockSeedVersion = STOCK_SEED_VERSION;
      }

      await setDoc(docRef, updates, { merge: true });

      if (needsSeed) {
        setInventory((prev) => ({
          ...prev,
          [product.id]: { ...(docSnap.data() || {}), ...updates },
        }));
      }
    } catch (err) {
      console.error(`Erro ao sincronizar estoque (${product.id}):`, err);
    }
  }, []);

  const loadInventory = useCallback(async (productId, options = {}) => {
    const {
      sizesAvailable = ['P', 'M', 'G', 'GG'],
      name = null,
      price = null,
    } = typeof options === 'object' && !Array.isArray(options)
      ? options
      : { sizesAvailable: options };

    try {
      const docRef = doc(db, 'inventory', productId);
      const docSnap = await getDoc(docRef);

      const meta = {
        ...(name ? { name } : {}),
        ...(price ? { price } : {}),
        updatedAt: serverTimestamp(),
      };

      if (docSnap.exists()) {
        const data = docSnap.data();

        if (name || price) {
          await setDoc(docRef, meta, { merge: true });
        }

        const merged = { ...data, ...meta };
        setInventory((prev) => ({ ...prev, [productId]: merged }));
        return merged;
      }

      const initialStock = {
        ...buildInitialStock(sizesAvailable, DEFAULT_STOCK),
        ...meta,
      };
      await setDoc(docRef, initialStock);
      setInventory((prev) => ({ ...prev, [productId]: initialStock }));
      return initialStock;
    } catch (err) {
      console.error('Erro ao carregar estoque:', err);
      return null;
    }
  }, []);

  const getStockForSize = useCallback(
    (productId, size) => {
      const productInventory = inventory[productId];
      if (!productInventory) return DEFAULT_STOCK;
      const stock = extractSizeStock(productInventory);
      return stock[size] ?? 0;
    },
    [inventory]
  );

  const decrementStock = useCallback(async (items) => {
    for (const item of items) {
      if (!item.productId || !item.size) continue;
      try {
        const docRef = doc(db, 'inventory', item.productId);
        await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(docRef);
          if (!docSnap.exists()) return;
          const data = docSnap.data();
          const currentStock = extractSizeStock(data)[item.size] ?? 0;
          const newStock = Math.max(0, currentStock - (item.quantity || 1));
          transaction.update(docRef, { [item.size]: newStock });
        });
        setInventory((prev) => {
          const productInv = prev[item.productId] || {};
          const currentStock = extractSizeStock(productInv)[item.size] ?? DEFAULT_STOCK;
          return {
            ...prev,
            [item.productId]: {
              ...productInv,
              [item.size]: Math.max(0, currentStock - (item.quantity || 1)),
            },
          };
        });
      } catch (err) {
        console.error(`Erro ao decrementar estoque de ${item.productId} - ${item.size}:`, err);
      }
    }
  }, []);

  const incrementStock = useCallback(async (items) => {
    for (const item of items) {
      if (!item.productId || !item.size) continue;
      try {
        const docRef = doc(db, 'inventory', item.productId);
        await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(docRef);
          if (!docSnap.exists()) return;
          const data = docSnap.data();
          const currentStock = extractSizeStock(data)[item.size] ?? 0;
          transaction.update(docRef, {
            [item.size]: currentStock + (item.quantity || 1),
          });
        });
        setInventory((prev) => {
          const productInv = prev[item.productId] || {};
          const currentStock = extractSizeStock(productInv)[item.size] ?? 0;
          return {
            ...prev,
            [item.productId]: {
              ...productInv,
              [item.size]: currentStock + (item.quantity || 1),
            },
          };
        });
      } catch (err) {
        console.error(`Erro ao restaurar estoque de ${item.productId} - ${item.size}:`, err);
        throw err;
      }
    }
  }, []);

  return (
    <InventoryContext.Provider
      value={{
        inventory,
        loadInventory,
        getStockForSize,
        decrementStock,
        incrementStock,
        syncProductInventory,
      }}
    >
      {children}
    </InventoryContext.Provider>
  );
};
